import React, { useEffect, useState } from "react"
import { ConnectionStatusBadge } from "../shopify/ConnectionStatusBadge"

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

const primaryBtn =
  "inline-flex items-center justify-center rounded-lg bg-brand-600 px-5 py-3 text-sm font-semibold text-white hover:bg-brand-700 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
const secondaryBtn =
  "inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-5 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
const dangerBtn =
  "inline-flex items-center justify-center rounded-lg border border-red-200 dark:border-red-900/50 px-5 py-3 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
const inputStyle =
  "w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500"

const fmtDate = (iso, fallback) =>
  iso ? new Date(iso).toLocaleString() : fallback

// Settings → Integrations panel for the Slack notification channel. Partners can
// connect via Slack OAuth (preferred) or paste a webhook URL directly.
export function SlackConnectionSettings({ connectionPath, copy }) {
  const [connection, setConnection] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState(null)
  const [error, setError] = useState(null)
  const [testSent, setTestSent] = useState(false)
  const [testBusy, setTestBusy] = useState(false)
  const [showWebhookForm, setShowWebhookForm] = useState(false)
  const [webhookUrl, setWebhookUrl] = useState("")
  const [channelName, setChannelName] = useState("")

  const fetchConnection = () =>
    fetch(connectionPath, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : null))

  useEffect(() => {
    let active = true
    fetchConnection()
      .then((data) => active && setConnection(data))
      .catch(() => active && setError(copy.errorGeneric))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [connectionPath, copy.errorGeneric])

  // OAuth: initiates the Slack OAuth flow, redirects browser to Slack.
  const connectOauth = async () => {
    setError(null)
    try {
      const res = await fetch(connectionPath, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
      if (res.ok) {
        const body = await res.json()
        window.location.href = body.redirect_url
      } else {
        setError(copy.errorGeneric)
      }
    } catch {
      setError(copy.errorGeneric)
    }
  }

  // Direct webhook: validate format, test, then save.
  const connectWebhook = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    setToast(null)
    try {
      const res = await fetch(connectionPath, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ webhook_url: webhookUrl, channel_name: channelName || undefined }),
      })
      if (res.ok) {
        const data = await res.json()
        setConnection(data)
        setShowWebhookForm(false)
        setWebhookUrl("")
        setChannelName("")
        setToast(copy.panel.webhookConnectedToast)
      } else {
        const body = await res.json().catch(() => ({}))
        setError(body.errors?.join?.(". ") || copy.errorGeneric)
      }
    } catch {
      setError(copy.errorGeneric)
    } finally {
      setBusy(false)
    }
  }

  const sendTest = async () => {
    if (testBusy) return
    setTestSent(false)
    setTestBusy(true)
    setError(null)
    try {
      const res = await fetch(`${connectionPath}/test`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
      if (res.ok) {
        setTestSent(true)
        setToast(null)
      } else {
        const body = await res.json().catch(() => ({}))
        setError(body.errors?.join?.(". ") || copy.errorGeneric)
      }
    } catch {
      setError(copy.errorGeneric)
    } finally {
      setTestBusy(false)
    }
  }

  const disconnect = async () => {
    if (busy) return
    if (!window.confirm(copy.panel.disconnectConfirm)) return
    setBusy(true)
    setError(null)
    try {
      const res = await fetch(connectionPath, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
      if (res.ok) {
        setConnection(null)
        setToast(copy.panel.disconnectedToast)
        setTestSent(false)
        setShowWebhookForm(false)
      } else {
        setError(copy.errorGeneric)
      }
    } catch {
      setError(copy.errorGeneric)
    } finally {
      setBusy(false)
    }
  }

  const [notifyAppEvents, setNotifyAppEvents] = useState(true)

  useEffect(() => {
    if (connection) setNotifyAppEvents(connection.notify_app_events ?? true)
  }, [connection])

  const toggleAppEvents = async () => {
    const next = !notifyAppEvents
    setNotifyAppEvents(next)
    try {
      const res = await fetch(connectionPath, {
        method: "PATCH",
        headers: { "X-CSRF-Token": csrfToken(), "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ notify_app_events: next }),
      })
      if (!res.ok) {
        setNotifyAppEvents(!next)
        setError(copy.errorGeneric)
      }
    } catch {
      setNotifyAppEvents(!next)
      setError(copy.errorGeneric)
    }
  }

  const status = connection?.status || "none"
  const isConnected = status === "connected"
  const isError = status === "error"

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{copy.panel.title}</h2>
        {!loading && <ConnectionStatusBadge status={status} label={copy.status[status]} />}
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">…</p>
      ) : connection ? (
        <>
          <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-gray-500 dark:text-gray-400">{copy.panel.channelLabel}</dt>
              <dd className="text-gray-900 dark:text-gray-100">{connection.channel_name}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">{copy.panel.teamLabel}</dt>
              <dd className="text-gray-900 dark:text-gray-100">{connection.team_name}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">{copy.panel.connectedAt}</dt>
              <dd className="text-gray-900 dark:text-gray-100">{fmtDate(connection.connected_at, copy.panel.never)}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">{copy.panel.lastVerifiedAt}</dt>
              <dd className="text-gray-900 dark:text-gray-100">{fmtDate(connection.last_verified_at, copy.panel.never)}</dd>
            </div>
            {connection.last_error && (
              <div className="sm:col-span-2">
                <dt className="text-gray-500 dark:text-gray-400">{copy.panel.lastError}</dt>
                <dd className="text-red-600 dark:text-red-400">{connection.last_error}</dd>
              </div>
            )}
          </dl>
          {isError && connection.last_error && (
            <div className="mt-4 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-700 dark:text-red-300">
              {copy.panel.errorHint}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{copy.appEventsToggle.label}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{copy.appEventsToggle.hint}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={notifyAppEvents}
              onClick={toggleAppEvents}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${notifyAppEvents ? "bg-brand-600" : "bg-gray-300 dark:bg-gray-600"}`}
            >
              <span className={`pointer-events-none inline-block h-4 w-4 translate-y-0 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${notifyAppEvents ? "translate-x-4" : "translate-x-0"}`} />
            </button>
          </div>
        </>
      ) : (
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">{copy.panel.none}</p>
      )}

      {toast && <p className="mt-4 text-sm text-green-700 dark:text-green-300">{toast}</p>}
      {testSent && <p className="mt-4 text-sm text-green-700 dark:text-green-300">{copy.testSent}</p>}
      {error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {!loading && !isConnected && showWebhookForm && (
        <div className="mt-6 space-y-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">{copy.webhook.hint}</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {copy.webhook.urlLabel}
            </label>
            <input
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder={copy.webhook.urlPlaceholder}
              className={inputStyle}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {copy.webhook.channelLabel}
            </label>
            <input
              type="text"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder={copy.webhook.channelPlaceholder}
              className={inputStyle}
            />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={connectWebhook} disabled={busy || !webhookUrl} className={primaryBtn}>
              {busy ? copy.webhook.connecting : copy.webhook.connect}
            </button>
            <button type="button" onClick={() => setShowWebhookForm(false)} className={secondaryBtn}>
              {copy.webhook.cancel}
            </button>
          </div>
        </div>
      )}

      {!loading && (
        <div className="mt-6 flex flex-col gap-3">
          {isConnected ? (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              <button type="button" onClick={disconnect} disabled={busy} className={dangerBtn}>
                {busy ? copy.panel.disconnecting : copy.panel.disconnect}
              </button>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button type="button" onClick={sendTest} disabled={testBusy} className={secondaryBtn}>
                  {testBusy ? copy.testSending : copy.testButton}
                </button>
              </div>
            </div>
          ) : connection ? (
            <div className="flex flex-col gap-3">
              <button type="button" onClick={connectOauth} className={primaryBtn}>
                {copy.connectOauth}
              </button>
              <button type="button" onClick={disconnect} disabled={busy} className={dangerBtn}>
                {busy ? copy.panel.disconnecting : copy.panel.disconnect}
              </button>
              {!showWebhookForm && (
                <button type="button" onClick={() => setShowWebhookForm(true)} className={secondaryBtn}>
                  {copy.webhook.toggle}
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <button type="button" onClick={connectOauth} className={primaryBtn}>
                {copy.connectOauth}
              </button>
              {!showWebhookForm && (
                <button type="button" onClick={() => setShowWebhookForm(true)} className={secondaryBtn}>
                  {copy.webhook.toggle}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
